import { Box, Divider, Grid, LinearProgress, Stack, Tab, Tabs, Typography } from "@mui/material";
import orderBy from "lodash/orderBy";
import type { FC } from "react";
import { useEffect, useState } from "react";
import "react-calendar/dist/Calendar.css";
import type { AdminProductOptionResponse } from "../productsApi";
import OptionPricesCalendar from "./OptionPricesCalendar";
import PriceListFromOtherOption from "./PriceListFromOtherOptionAlert";
import PriceModeSelector, { PriceMode } from "./PriceModeSelector";
import { useOptionPrices } from "./useOptionPrices";

function a11yProps(id: string) {
  return {
    id: `vertical-tab-${id}`,
    "aria-controls": `vertical-tabpanel-${id}`,
  };
}

interface OptionPricesReadOnlyProps {
  readonly productId: string;
  readonly optionId: string;
  readonly useDynamicPrices: boolean;
  readonly option?: AdminProductOptionResponse;
}

const OptionPricesReadOnly: FC<OptionPricesReadOnlyProps> = ({ productId, optionId, useDynamicPrices, option }) => {
  const { data, isLoading } = useOptionPrices(productId, optionId);
  const [priceMode, setPriceMode] = useState(PriceMode.SELLING_PRICE);

  return (
    <Grid size={{ xs: 12, lg: 8 }} container spacing={2}>
      {option && (
        <Grid size={12} container spacing={1}>
          <Grid size={12}>
            <Typography
              variant="subtitle2"
              sx={{
                color: "text.secondary",
              }}
            >
              Szczegóły opcji
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">
              <strong>Nazwa</strong>: {option.name}
              {option.unit ? ` (${option.unit})` : ""}
            </Typography>
          </Grid>
          {option.providerDetails.optionName ? (
            <Grid size={12}>
              <Typography variant="body2">
                <strong>Nazwa dla dostawcy</strong>: {option.providerDetails.optionName}
              </Typography>
            </Grid>
          ) : null}
          {useDynamicPrices && option.providerDetails.optionId ? (
            <Grid size={12}>
              <Typography variant="body2">
                <strong>ID u dostawcy</strong>: {option.providerDetails.optionId}
              </Typography>
            </Grid>
          ) : null}
          {useDynamicPrices ? (
            <Grid size={12}>
              <Typography variant="body2">
                <strong>Przesunięcie daty</strong>: {option.providerDetails.dateMatchShift ?? 0}
              </Typography>
            </Grid>
          ) : null}
          {option.info?.polish ? (
            <Grid size={12}>
              <Typography variant="body2">
                <strong>Info</strong>: {option.info.polish}
              </Typography>
            </Grid>
          ) : null}
          {option.info?.english ? (
            <Grid size={12}>
              <Typography variant="body2">
                <strong>Info (eng)</strong>: {option.info.english}
              </Typography>
            </Grid>
          ) : null}
          <Grid size={12}>
            <Divider />
          </Grid>
        </Grid>
      )}
      <Grid size={12}>
        <PriceModeSelector value={priceMode} onChange={(v) => setPriceMode(v)} />
      </Grid>
      {isLoading && (
        <Grid size={12}>
          <LinearProgress />
        </Grid>
      )}
      {data?.usePricesFrom && (
        <Grid size={12}>
          <PriceListFromOtherOption productId={data.usePricesFrom.productId} optionId={data.usePricesFrom.optionId} />
        </Grid>
      )}
      {data?.prices && !data.usePricesFrom && (
        <Grid size={12} data-testid="price-list-month">
          <OptionPricesCalendar
            priceMode={priceMode}
            prices={data.prices}
            maxDate={undefined}
            onDaysValueChange={() => {}}
          />
        </Grid>
      )}
    </Grid>
  );
};

interface Props {
  readonly productId: string;
  readonly provider: { readonly id: string; readonly useDynamicPrices: boolean };
  readonly options: AdminProductOptionResponse[];
}

const ProductOptionsView: FC<Props> = ({ productId, provider, options }) => {
  const [selectedOption, setSelectedOption] = useState<string>();

  useEffect(() => {
    if (options.length > 0) {
      setSelectedOption(options[0].id);
    }
  }, [options]);

  const sortedOptions = orderBy(options, (o) => o.name.toLowerCase());
  const selectedOptionData = sortedOptions.find((o) => o.id === selectedOption) ?? sortedOptions[0];

  if (options.length === 0) {
    return (
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
        }}
      >
        Ten produkt nie ma żadnych opcji z cennikiem.
      </Typography>
    );
  }

  return (
    <Grid container spacing={2} className="readonly-product-option">
      <Grid size={12}>
        <Typography variant="h6">Cennik</Typography>
      </Grid>
      <Grid size={12}>
        <Stack direction="row">
          <Tabs
            orientation="vertical"
            variant="scrollable"
            value={selectedOption ?? sortedOptions[0]?.id}
            onChange={(_, newValue) => setSelectedOption(newValue)}
            aria-label="Opcje produktu"
            sx={{ borderRight: 1, borderColor: "divider", flexShrink: 0 }}
          >
            {sortedOptions.map((o) => (
              <Tab key={o.id} label={o.name} value={o.id} {...a11yProps(o.id)} />
            ))}
          </Tabs>
          {selectedOption && (
            <Box
              role="tabpanel"
              id={`vertical-tabpanel-${selectedOption}`}
              aria-labelledby={`vertical-tab-${selectedOption}`}
              sx={{
                padding: 2,
                flexGrow: 1,
              }}
            >
              <OptionPricesReadOnly
                productId={productId}
                optionId={selectedOption}
                useDynamicPrices={provider.useDynamicPrices}
                option={selectedOptionData}
              />
            </Box>
          )}
        </Stack>
      </Grid>
    </Grid>
  );
};

export default ProductOptionsView;
